import React, { useCallback, useEffect, useState } from 'react';
import api from '../../services/api';
import { useTri, EnteteTri } from './TriTableau';
import { formatDateFr } from '../../utils/planningTime';
import './RdvNonHonores.css';

// ═══════════════════════════════════════════════════════════════════════════
// Tableaux ▸ RDV non honorés — la file de rappel du call center.
//
// Le réceptionnaire déclare un no-show au clic droit sur la carte (le dossier
// quitte la grille) ; cette vue liste ces dossiers pour rappeler le client.
// « ✓ Traité » solde sans replanifier (client rappelé, ne revient pas…) ; la
// replanification par drag depuis la section du panneau solde d'elle-même.
// Même logique que « RDV à confirmer » : filtres en tête, table triable,
// compteur remonté au parent pour le badge du menu Tableaux.
// ═══════════════════════════════════════════════════════════════════════════

const MOTIFS = { non_presente: "Ne s'est pas présenté", annule: 'A annulé', autre: 'Autre' };

export default function RdvNonHonores({ onOpenIntervention, onCountChange }) {
  const [filtre, setFiltre] = useState('a_rappeler'); // 'a_rappeler' | 'traites'
  const [liste, setListe] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState('');
  const [busy, setBusy] = useState(null);
  const tri = useTri(null, 'asc');

  const charger = useCallback(async () => {
    setChargement(true);
    setErreur('');
    try {
      const { data } = await api.get(`/planning/non-honores?traites=${filtre === 'traites' ? '1' : '0'}`);
      setListe(data.non_honores || []);
      if (filtre === 'a_rappeler' && onCountChange) onCountChange((data.non_honores || []).length);
    } catch (e) {
      console.error('[non-honores]', e);
      setErreur(e.response?.data?.error || 'Chargement impossible');
    } finally {
      setChargement(false);
    }
    // onCountChange volontairement HORS dépendances : le parent recrée la
    // fonction à chaque rendu (tick d'horloge…), la garder ici provoquerait un
    // refetch en boucle.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtre]);

  useEffect(() => { charger(); }, [charger]);

  const traiter = async (d) => {
    if (busy) return;
    setBusy(d.id);
    try {
      await api.patch(`/planning/interventions/${d.id}/non-honore/traite`);
      await charger();
    } catch (e) {
      console.error('[non-honores/traite]', e);
    } finally {
      setBusy(null);
    }
  };

  const fmtTel = (t) => {
    const c = String(t || '').replace(/\D/g, '');
    return c.length === 10 ? c.replace(/(\d{2})(?=\d)/g, '$1 ') : (t || '');
  };

  const lignes = tri.appliquer(liste, {
    rdv: d => `${d.date || ''} ${d.heure_reception || d.heure_debut || ''}`,
    client: d => d.client_nom || '',
    vehicule: d => `${d.marque || ''} ${d.immat || ''}`,
    motif: d => d.non_honore_motif || '',
    declare: d => d.non_honore_at || '',
  });

  return (
    <div className="rnh">
      <div className="rnh-entete">
        <h2>🚫 RDV non honorés</h2>
        <div className="rnh-filtres">
          <button type="button" className={`rnh-filtre${filtre === 'a_rappeler' ? ' is-active' : ''}`}
            onClick={() => setFiltre('a_rappeler')}>À rappeler</button>
          <button type="button" className={`rnh-filtre${filtre === 'traites' ? ' is-active' : ''}`}
            onClick={() => setFiltre('traites')}>Traités</button>
        </div>
      </div>

      {erreur && <p className="rnh-note">⚠️ {erreur}</p>}
      {chargement && liste.length === 0 && <p className="rnh-note">Chargement…</p>}
      {!chargement && !erreur && liste.length === 0 && (
        <p className="rnh-note">
          {filtre === 'a_rappeler'
            ? 'Aucun rendez-vous non honoré à rappeler — tout est à jour.'
            : 'Aucun dossier traité.'}
        </p>
      )}

      {liste.length > 0 && (
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <EnteteTri tri={tri} cle="rdv">RDV manqué</EnteteTri>
                <EnteteTri tri={tri} cle="client">Client</EnteteTri>
                <th>Téléphone</th>
                <EnteteTri tri={tri} cle="vehicule">Véhicule</EnteteTri>
                <EnteteTri tri={tri} cle="motif">Motif</EnteteTri>
                <th>Commentaire</th>
                <EnteteTri tri={tri} cle="declare">Déclaré</EnteteTri>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {lignes.map(d => (
                <tr key={d.id}>
                  <td className="rnh-nowrap">
                    {formatDateFr(d.date)}{(d.heure_reception || d.heure_debut) ? ` · ${d.heure_reception || d.heure_debut}` : ''}
                  </td>
                  <td>{[d.client_prenom, d.client_nom].filter(Boolean).join(' ') || '—'}</td>
                  <td className="rnh-nowrap">
                    {d.client_tel
                      ? <a className="rnh-tel" href={`tel:${String(d.client_tel).replace(/\D/g, '')}`}>{fmtTel(d.client_tel)}</a>
                      : <span className="rnh-muted">—</span>}
                  </td>
                  <td>{[d.marque, d.modele].filter(Boolean).join(' ') || '—'}{d.immat ? <span className="rnh-immat">{d.immat}</span> : null}</td>
                  <td>{MOTIFS[d.non_honore_motif] || d.non_honore_motif || '—'}</td>
                  <td className="rnh-commentaire" title={d.non_honore_commentaire || ''}>{d.non_honore_commentaire || <span className="rnh-muted">—</span>}</td>
                  <td className="rnh-nowrap rnh-muted">
                    {d.non_honore_at ? new Date(d.non_honore_at).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—'}
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button className="btn-icon" title="Ouvrir le dossier"
                        onClick={() => onOpenIntervention && onOpenIntervention(d)}>👁️</button>
                      {filtre === 'a_rappeler' && (
                        <button className="btn-icon" title="Traité — client rappelé / dossier soldé"
                          disabled={busy === d.id}
                          onClick={() => traiter(d)}>{busy === d.id ? '⏳' : '✅'}</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
